import mixpanel from 'mixpanel-browser';


// Common event names to avoid typos and ensure consistency
export enum EventName {
  // Auth related events
  USER_LOGIN = 'UserLogin',
  USER_LOGIN_FAILED = 'UserLoginFailed',
  USER_REGISTRATION_STARTED = 'UserRegistrationStarted',
  USER_REGISTRATION_FAILED = 'UserRegistrationFailed',
  USER_SIGN_UP = 'UserSignUp',
  USER_ACCOUNT_CONFIRMED = 'UserAccountConfirmed',
  CONFIRMATION_CODE_RESENT = 'ConfirmationCodeResent',
  USER_LOGOUT = 'UserLogout',
  
  // MFA related events
  MFA_VERIFICATION_SUCCEEDED = 'MfaVerificationSucceeded',
  MFA_VERIFICATION_FAILED = 'MfaVerificationFailed',
  MFA_SETUP_STARTED = 'MfaSetupStarted',
  MFA_SETUP_COMPLETED = 'MfaSetupCompleted',
  
  // Password related events
  PASSWORD_RESET_REQUESTED = 'PasswordResetRequested',
  PASSWORD_RESET_COMPLETED = 'PasswordResetCompleted',
  
  // Project/Workspace related events
  WORKSPACE_CREATED = 'WorkspaceCreated',
  PROJECT_CREATION_STARTED = 'ProjectCreationStarted',
  PROJECT_CREATION_COMPLETED = 'ProjectCreationCompleted',
  PROJECT_CREATION_FAILED = 'ProjectCreationFailed',
  PROJECT_TEMPLATE_SELECTED = 'ProjectTemplateSelected',
  
  // Page view events
  PAGE_VIEW = 'PageView',
  ACCOUNT_SETTINGS_VIEWED = 'AccountSettingsViewed'
}

const enableTracking = process.env.REACT_APP_MIXPANEL_ENABLE_TRACKING === 'true';
const mixpanelToken = process.env.REACT_APP_MIXPANEL_TOKEN;
const environment = process.env.REACT_APP_MIXPANEL_ENVIRONMENT || 'development';
const shouldTrack: boolean = Boolean(mixpanelToken?.length) && enableTracking;

if(mixpanelToken && shouldTrack) {
  mixpanel.init(mixpanelToken, {
    debug: false,
    track_pageview: false, // this is enabled explictly in the trackPageView function in the react router
    persistence: 'localStorage',
    record_sessions_percent: 100,
    record_mask_text_selector: "",
    record_block_selector: "",
    record_idle_timeout_ms: 3600000,
    record_max_ms: 86400000,
    record_canvas: true,    
    autocapture: true,
  });
  
  mixpanel.register({
    environment,
  });
}

// Types for better type checking and auto-completion
export interface MixpanelUserProperties {
  $name?: string;
  $email?: string;
  $userId?: string;
  $created?: string;
  [key: string]: any;
}

export type EventProperties = Record<string, any>;

/**
 * Wrapper for Mixpanel functionality that ensures tracking only happens when enabled
 */
const MixpanelService = {
  /**
   * Identify user with their unique ID
   */
  identify: (userId: string) => {
    try {
      if (shouldTrack) {
        mixpanel?.identify(userId);
      }
    } catch (error) {
      console.error('Mixpanel identify error');
    }
  },

  /**
   * Track event with optional properties
   */
  track: (eventName: EventName | string, properties: EventProperties = {}) => {
    try {
      if (shouldTrack) {
        mixpanel?.track(eventName, properties);
      }
    } catch (error) {
      console.error('Mixpanel track error');
    }
  },

  /**
   * Track page view with current URL and page title
   */
  trackPageView: (path: string, properties: EventProperties = {}) => {
    try {
      if (shouldTrack) {
        mixpanel?.track(EventName.PAGE_VIEW, {
          path,
          url: window.location.href,
          title: document.title,
          referrer: document.referrer,
          ...properties
        });
      }
    } catch (error) {
      console.error('Mixpanel trackPageView error');
    }
  },

  /**
   * Set user properties
   */
  setUserProperties: (properties: MixpanelUserProperties) => {
    try {
      if (shouldTrack) {
        mixpanel?.people?.set(properties);
      }
    } catch (error) {
      console.error('Mixpanel setUserProperties error');
    }
  },

  /**
   * Set user's email
   */
  setUserEmail: (email: string) => {
    try {
      if (shouldTrack) {
        mixpanel?.people?.set({ $email: email });
      }
    } catch (error) {
      console.error('Mixpanel setUserEmail error');
    }
  },

  /**
   * Set user's name
   */
  setUserName: (name: string) => {
    try {
      if (shouldTrack) {
        mixpanel?.people?.set({ $name: name });
      }
    } catch (error) {
      console.error('Mixpanel setUserName error');
    }
  },

  /**
   * Reset current user - call when user logs out
   */
  reset: () => {
    try {
      if (shouldTrack) {
        mixpanel?.reset();
      }
    } catch (error) {
      console.error('Mixpanel reset error');
    }
  },

  /**
   * Create alias for user (for signup flow)
   */
  alias: (userId: string) => {
    try {
      if (shouldTrack) {
        mixpanel?.alias(userId);
      }
    } catch (error) {
      console.error('Mixpanel alias error');
    }
  }
};

export default MixpanelService;



/*

Docs:
  people.set()
  If a profile property already exists, it will be overwritten with the latest value provided in the method.
  If a profile property does not exist, it will be added to the profile.

  Other Types Of Profile Updates
  The .people.set_once() method set profile properties only if they do not exist yet. If it is setting a profile property that already exists, it will be ignored.
  The .people.append() method append values to a list profile property. Use this method to add additional values to an existing list property instead of redefining the entire list.
  The .people.increment() method increments a numeric property by a whole number.

*/
